import Card from '@material-ui/core/Card';
import IconButton from '@material-ui/core/IconButton';
import { withStyles } from '@material-ui/core/styles';
import RefreshIcon from '@material-ui/icons/Autorenew';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';
import ViewListIcon from '@material-ui/icons/ViewList';
import { Entity } from '@sketchpixy/rubix/lib/L20n';
import _ from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import { TABLE_HEADER_COLOR } from '../../_config/consts';
import * as MksActions from '../../redux/actions/mks.actions';
import * as UtilsActions from '../../redux/actions/utils.actions';
import EmptyView from '../../components/EmptyView.jsx';
import ListLoadingView from '../../components/ListLoadingView.jsx';
import MksRelationsMatrixHeader from '../../components/MKS/MksRelationsMatrixHeader.jsx';
import MksCylinderDetailsView from '../../components/MKS/MksCylinderDetailsView.jsx';
import OperationalView from '../../components/OperationalView/OperationalView.jsx';
import { GridOnOutlined } from '@material-ui/icons';
import { push } from 'react-router-redux';

const styles = theme => ({
  matrixContainer: {
    position: 'relative',
    overflow: 'auto',
    maxHeight: 'calc(100vh - 100px)',
    minHeight: 'calc(100vh - 100px)',
    border: '1px solid #e0e0e0',
  },
  matrixTable: {
    borderCollapse: 'collapse',
    fontSize: '12px',
    transformOrigin: 'top left',
  },
  matrixHeaderRow: {
    backgroundColor: TABLE_HEADER_COLOR,
    position: 'sticky',
    top: 0,
    zIndex: 3,
  },
  matrixHeaderCell: {
    padding: '8px 12px',
    fontWeight: 'bold',
    color: '#3f3f3f',
    border: '1px solid #d0d0d0',
    textAlign: 'center',
    minWidth: '80px',
    position: 'sticky',
    top: 0,
    backgroundColor: TABLE_HEADER_COLOR,
    zIndex: 2,
  },
  matrixFirstColumnHeader: {
    position: 'sticky',
    left: 0,
    zIndex: 4,
    backgroundColor: TABLE_HEADER_COLOR,
    minWidth: '200px',
    maxWidth: '200px',
    textAlign: 'left',
  },
  matrixRow: {
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    '&:nth-of-type(even)': {
      backgroundColor: '#fafafa',
    },
    '&:hover': {
      backgroundColor: '#e3f2fd',
    },
  },
  matrixCell: {
    padding: '8px 12px',
    border: '1px solid #e0e0e0',
    textAlign: 'center',
    color: '#3f3f3f',
    minWidth: '80px',
  },
  matrixFirstColumn: {
    position: 'sticky',
    left: 0,
    backgroundColor: 'white',
    fontWeight: 'bold',
    zIndex: 1,
    minWidth: '200px',
    maxWidth: '200px',
    textAlign: 'left',
    borderRight: '2px solid #d0d0d0',
  },
  matrixFirstColumnEven: {
    backgroundColor: '#fafafa',
  },
  relationDot: {
    fontSize: '24px',
    color: '#4CAF50',
    fontWeight: 'bold',
  },
  controlsBar: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '15px 20px',
    backgroundColor: TABLE_HEADER_COLOR,
  },
  zoomControls: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '10px',
  },
  zoomLabel: {
    fontWeight: 'bold',
    color: '#3f3f3f',
    marginRight: '10px',
  },
  tableColumnText: {
    fontWeight: 'bold',
    color: '#3f3f3f',
  },
});

@connect(state => ({
  viewLoading: state.utils.viewLoading,
  mks: state.mks,
  themeName: state.settings.items.theme.data.themeName,
}))
class MksRelationsMatrix extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      zoom: 1,
      selectedCylinderIds: [],
      selectedKeyIds: [],
      isDetailsViewOpen: false,
      selectedCylinderForDetails: null,
    };
  }

  async componentDidMount() {
    const { dispatch, mks } = this.props;
    
    // Only fetch if relations are empty
    const relationsContent = mks.relations.data && mks.relations.data.content ? mks.relations.data.content : [];
    const cylindersContent = mks.cylinders.data && mks.cylinders.data.content ? mks.cylinders.data.content : [];
    const keysContent = mks.keys.data && mks.keys.data.content ? mks.keys.data.content : [];
    
    const needsDataFetch = _.isEmpty(relationsContent) || _.isEmpty(cylindersContent) || _.isEmpty(keysContent);
    
    if (needsDataFetch) {
      await this.onFetchRelationsData();
    }
  }

  async onFetchRelationsData() {
    const { dispatch } = this.props;
    dispatch(UtilsActions.setViewLoading(true));
    try {
      await dispatch(MksActions.fetchCylinders(0, 10000));
      await dispatch(MksActions.fetchKeys(0, 10000));
      await dispatch(MksActions.fetchMechanicalCylinderKeyRelations(0, 10000));
    } catch (error) {
      console.error('Error fetching relations data:', error);
    } finally {
      dispatch(UtilsActions.setViewLoading(false));
    }
  }

  onZoomIn() {
    this.setState(prevState => ({
      zoom: Math.min(prevState.zoom + 0.1, 2),
    }));
  }

  onZoomOut() {
    this.setState(prevState => ({
      zoom: Math.max(prevState.zoom - 0.1, 0.5),
    }));
  }

  onResetZoom() {
    this.setState({ zoom: 1 });
  }

  onSelectCylinders(selectedCylinders) {
    const selectedIds = selectedCylinders ? _.map(selectedCylinders, c => c.value) : [];
    this.setState({ selectedCylinderIds: selectedIds });
  }

  onSelectKeys(selectedKeys) {
    const selectedIds = selectedKeys ? _.map(selectedKeys, k => k.value) : [];
    this.setState({ selectedKeyIds: selectedIds });
  }

  onSearchReset() {
    this.setState({ 
      selectedCylinderIds: [], 
      selectedKeyIds: [] 
    });
  }

  async onSelectCylinderRow(cylinder) {
    const { dispatch } = this.props;
    dispatch(UtilsActions.setSpinnerVisibile(true));
    try {
      const relationsData = await dispatch(MksActions.fetchCylinderRelations(cylinder.id));
      const keys = relationsData && relationsData.content ? _.map(relationsData.content, key => key.key) : [];
      const cylinderDetail = {
        ...cylinder,
        associatedKeys: keys,
      };
      this.setState({ 
        isDetailsViewOpen: true,
        selectedCylinderForDetails: cylinderDetail,
      });
    } catch (error) {
      console.error('Error fetching cylinder details:', error);
    } finally {
      dispatch(UtilsActions.setSpinnerVisibile(false));
    }
  }

  onCloseDetailsView() {
    this.setState({ 
      isDetailsViewOpen: false,
      selectedCylinderForDetails: null,
    });
  }

  onRequestCylinderDuplication(cylinder) {
    const { dispatch } = this.props;
    dispatch(push(`/mksRequests?cylinderId=${cylinder.id}`));
  }

  buildMatrixData() {
    const { mks } = this.props;
    const { selectedCylinderIds, selectedKeyIds } = this.state;
    
    const relationsContent = mks.relations.data && mks.relations.data.content ? mks.relations.data.content : [];
    const cylindersContent = mks.cylinders.data && mks.cylinders.data.content ? mks.cylinders.data.content : [];
    const keysContent = mks.keys.data && mks.keys.data.content ? mks.keys.data.content : [];

    // Filter cylinders by selected IDs if any
    let filteredCylinders = cylindersContent;
    if (!_.isEmpty(selectedCylinderIds)) {
      filteredCylinders = _.filter(cylindersContent, c => selectedCylinderIds.includes(c.id));
    }

    // Filter keys by selected IDs if any
    let filteredKeys = keysContent;
    if (!_.isEmpty(selectedKeyIds)) {
      filteredKeys = _.filter(keysContent, k => selectedKeyIds.includes(k.id));
    }

    // Sort cylinders by doorName
    const sortedCylinders = _.sortBy(filteredCylinders, 'doorName');
    
    // Get unique keys and sort by keyFunction
    const uniqueKeys = _.uniqBy(filteredKeys, 'keyFunction');
    const sortedKeys = _.sortBy(uniqueKeys, 'keyFunction');

    // Build relation map for quick lookup
    // Key: cylinderId-keyId, Value: true
    const relationMap = {};
    _.forEach(relationsContent, (relation) => {
      const key = `${relation.cylinderId}-${relation.keyId}`;
      relationMap[key] = true;
    });

    return {
      cylinders: sortedCylinders,
      keys: sortedKeys,
      relationMap,
    };
  }

  onCheckRelation(cylinderId, keyId, relationMap) {
    const key = `${cylinderId}-${keyId}`;
    return relationMap[key] === true;
  }

  render() {
    const {
      classes,
      viewLoading,
      mks,
      themeName,
    } = this.props;

    const { zoom, isDetailsViewOpen, selectedCylinderForDetails } = this.state;

    const relationsContent = mks.relations.data && mks.relations.data.content ? mks.relations.data.content : [];
    const cylindersContent = mks.cylinders.data && mks.cylinders.data.content ? mks.cylinders.data.content : [];
    const keysContent = mks.keys.data && mks.keys.data.content ? mks.keys.data.content : [];

    const hasData = !_.isEmpty(relationsContent) && !_.isEmpty(cylindersContent) && !_.isEmpty(keysContent);

    const { cylinders, keys, relationMap } = hasData ? this.buildMatrixData() : { cylinders: [], keys: [], relationMap: {} };

    return (
      <div>
        <MksRelationsMatrixHeader
          themeName={themeName}
          cylinders={cylindersContent}
          keys={keysContent}
          onSelectCylinders={(selectedCylinders) => this.onSelectCylinders(selectedCylinders)}
          onSelectKeys={(selectedKeys) => this.onSelectKeys(selectedKeys)}
          onSearchReset={() => this.onSearchReset()}
          onRefresh={() => this.onFetchRelationsData()}
          entitiesNumberSection={
            <div style={{ marginRight: 20, marginTop: 13, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <GridOnOutlined style={{ fontSize: 25 }} />
              <h4 style={{ marginTop: 0 }}>{relationsContent.length}</h4>
            </div>
          }
        />
      <div className="list-view details-section" style={{ top: 140, paddingBottom: 20, width: '87%' }}>
        <div>
          {hasData ? (
            <Card elevation={4}>
              <div className={classes.controlsBar}>
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <h3 className={classes.tableColumnText} style={{ marginTop: 0, marginBottom: 0, marginRight: 15 }}>
                    <Entity entity="mksRelations" />
                  </h3>
                  <IconButton onClick={() => this.onFetchRelationsData()} style={{ padding: 8 }}>
                    <RefreshIcon style={{ color: '#3f3f3f', fontSize: 25 }} />
                  </IconButton>
                </div>
                <div className={classes.zoomControls}>
                  <span className={classes.zoomLabel}>
                    Zoom: {Math.round(zoom * 100)}%
                  </span>
                  <IconButton onClick={() => this.onZoomOut()} style={{ padding: 8 }}>
                    <ZoomOutIcon style={{ color: '#3f3f3f', fontSize: 25 }} />
                  </IconButton>
                  <IconButton onClick={() => this.onResetZoom()} style={{ padding: 8 }}>
                    <span style={{ color: '#3f3f3f', fontSize: 14, fontWeight: 'bold' }}>100%</span>
                  </IconButton>
                  <IconButton onClick={() => this.onZoomIn()} style={{ padding: 8 }}>
                    <ZoomInIcon style={{ color: '#3f3f3f', fontSize: 25 }} />
                  </IconButton>
                </div>
              </div>
              <div className={classes.matrixContainer}>
                <table 
                  className={classes.matrixTable}
                  style={{ transform: `scale(${zoom})` }}
                >
                  <thead>
                    <tr className={classes.matrixHeaderRow}>
                      <th className={`${classes.matrixHeaderCell} ${classes.matrixFirstColumnHeader}`}>
                        <Entity entity="mksCylinders" /> / <Entity entity="mksKeys" />
                      </th>
                      {_.map(keys, (key) => (
                        <th key={key.id} className={classes.matrixHeaderCell}>
                          {key.keyFunction || '---'}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {_.map(cylinders, (cylinder, rowIndex) => (
                      <tr 
                        key={cylinder.id} 
                        className={classes.matrixRow}
                        onClick={() => this.onSelectCylinderRow(cylinder)}
                      >
                        <td 
                          className={`${classes.matrixCell} ${classes.matrixFirstColumn} ${
                            rowIndex % 2 === 1 ? classes.matrixFirstColumnEven : ''
                          }`}
                        >
                          {cylinder.doorName || '---'}
                        </td>
                        {_.map(keys, (key) => (
                          <td key={`${cylinder.id}-${key.id}`} className={classes.matrixCell}>
                            {this.onCheckRelation(cylinder.id, key.id, relationMap) ? (
                              <span className={classes.relationDot}>•</span>
                            ) : (
                              ''
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ padding: '15px 20px', backgroundColor: TABLE_HEADER_COLOR, borderTop: '1px solid #d0d0d0' }}>
                <h5 style={{ margin: 0, color: '#3f3f3f' }}>
                  <Entity entity="mksCylinders" />: {cylinders.length} | <Entity entity="mksKeys" />: {keys.length} | <Entity entity="mksRelations" />: {relationsContent.length}
                </h5>
              </div>
            </Card>
          ) : (
            <div>
              {!viewLoading ? (
                <EmptyView
                  title="noMksRelations"
                  subtitle="noMksRelationsSubtitle"
                  iconName="icon-simple-line-icons-grid"
                />
              ) : (
                <ListLoadingView />
              )}
            </div>
          )}
        </div>
      </div>
      <OperationalView
        themeName={themeName}
        isVisible={isDetailsViewOpen}
        onClose={() => this.onCloseDetailsView()}
        title={<Entity entity="cylinderDetails" />}
      >
        <div style={{ padding: 5, paddingTop: 0 }}>
          {selectedCylinderForDetails && (
            <MksCylinderDetailsView 
              cylinderData={selectedCylinderForDetails}
              onRequestCylinderDuplication={(cylinder) => this.onRequestCylinderDuplication(cylinder)}
            />
          )}
        </div>
      </OperationalView>
      </div>
    );
  }
}

export default withStyles(styles)(MksRelationsMatrix);

